import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listResource, getResource, createResource, updateResource } from '@diggens/api-client';
import type { PortfolioCompany } from '@diggens/types';

export function usePortfolioCompanies(fundId?: string) {
  return useQuery({
    queryKey: ['portfolioCompanies', fundId],
    queryFn: async () => {
      const params: Record<string, string | undefined> = { fund_id: fundId };
      const { data } = await listResource<PortfolioCompany>('portfolio_companies', params);
      return data;
    },
  });
}

export function usePortfolioCompany(companyId: string) {
  return useQuery({
    queryKey: ['portfolioCompanies', companyId],
    queryFn: () => getResource<PortfolioCompany>('portfolio_companies', companyId),
    enabled: !!companyId,
  });
}

export function useCreatePortfolioCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { tenant_id: string; fund_id: string; name: string; website?: string | null; sector?: string | null; external_id?: string | null; investment_date?: string | null }) =>
      createResource<PortfolioCompany>('portfolio_companies', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolioCompanies'] });
    },
  });
}

export function useUpdatePortfolioCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { companyId: string; updates: Partial<PortfolioCompany> }) =>
      updateResource<PortfolioCompany>('portfolio_companies', variables.companyId, variables.updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['portfolioCompanies'] });
      queryClient.invalidateQueries({ queryKey: ['portfolioCompanies', variables.companyId] });
    },
  });
}
